import { useState } from 'react';
import { Card, Stack } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useCanWrite } from '@/access/useCanWrite';
import { EmptyState } from '@/components/shell/EmptyState';
import { LoadingState } from '@/components/shell/LoadingState';
import { PageTitle } from '@/components/shell/PageTitle';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { TestBenchLaunchForm } from './TestBenchLaunchForm';
import { TestBenchRecentPayments } from './TestBenchRecentPayments';
import { TestBenchTimelineList } from './TestBenchTimelineList';
import { useTestBenchAvailability } from './useTestBenchAvailability';

/**
 * "Probar pago" — the payment test-bench screen (run-6). Launches a real
 * signed payment through the platform's own public API (backed by the
 * seeded `test-bench` credential, server-side) and follows it live via the
 * timeline poll. Hidden entirely from navigation while the backend flag is
 * off; reachable-by-URL visitors instead see the same designed empty state
 * as an unavailable section, never a broken form.
 */
export function TestBenchPage() {
  const translate = useTranslate();
  const canWrite = useCanWrite();
  const { available, loading: availabilityLoading } = useTestBenchAvailability();
  const [selectedIntentId, setSelectedIntentId] = useState<string>();
  const [recentRefreshToken, setRecentRefreshToken] = useState(0);

  if (availabilityLoading) {
    return (
      <Stack spacing={1.5}>
        <Card>
          <LoadingState variant="card" />
        </Card>
      </Stack>
    );
  }

  if (!available) {
    return (
      <Stack spacing={1.5}>
        <Card>
          <EmptyState title={translate('pay.testBench.unavailable.title')} body={translate('pay.testBench.unavailable.body')} />
        </Card>
      </Stack>
    );
  }

  function handleLaunched(intentId: string) {
    setSelectedIntentId(intentId);
    setRecentRefreshToken((token) => token + 1);
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        left={<PageTitle title={translate('pay.testBench.pageTitle')} subtitle={translate('pay.testBench.pageSubtitle')} />}
      />
      <TestBenchLaunchForm canWrite={canWrite} onLaunched={handleLaunched} />
      <TestBenchRecentPayments key={recentRefreshToken} selectedIntentId={selectedIntentId} onSelect={setSelectedIntentId} />
      {selectedIntentId && <TestBenchTimelineList intentId={selectedIntentId} />}
    </Stack>
  );
}
